<template>
  <div class="do-scheduler" ref="doScheduler" :style="{ maxHeight: schedulerMaxHeight + 'px' }">
    <ul class="do-scheduler__list" :style="{'width': slotWidth * 48 + 'px'}">
      <ul class="scheduler__header__times">
        <li v-for="number in hoursInLocale" :style="{minWidth: slotWidth * 2 + 'px'}">{{ number }}</li>
      </ul>
      <li class="do-scheduler__list__item"
          v-for="calendar in calendarsWithEvents"
          :key="calendar.id"
          :style="{height: (slotHeight * calendar.vColumns) + 'px'}">
        <div class="do-scheduler__list__item__name">
          <h4>{{ calendar.name }}</h4>
        </div>
        <ul class="do-scheduler__item__events"
            :style="{'width': slotWidth * 48 + 'px', height: (slotHeight * calendar.vColumns) + 'px'}">
          <li class="do-scheduler__item__events__presence"
              v-for="presence in calendar.vPresence"
              :style="{left: presence.left + 'px', right: presence.right + 'px'}">
          </li>
          <li v-for="event in calendar.vEvents"
              :key="event.id"
              class="do-scheduler__item__events__event"
              :style="getEventStyle(event)">
            <div class="do-scheduler__item__events__event__title">{{ event.title }}</div>
            <div class="do-scheduler__item__events__event__klass">{{ event.vKlasses }}</div>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  props: {
    events: {
      type: Array,
      default: () => []
    },
    klassCalendars: {
      type: Array,
      default: () => []
    },
    userCalendars: {
      type: Array,
      default: () => []
    },
    allCalendarsInHash: {
      type: Object,
      default: () => {
      }
    }
  },
  data () {
    return {
      calendarType: 'user',
      slotHeight: 35,
      slotWidth: 50,
      eventHeightInPx: 28,
      rows: 1,
      schedulerMaxHeight: '100vh'
    }
  },
  computed: {
    hoursInLocale () {
      let hours = []
      for (let i = 0; i < 24; i++) {
        const hour = moment().set({hour: i, minutes: 0, seconds: 0}).format('LT')
        hours.push(hour)
      }
      return hours
    },
    presenceAndAbsenceEvents () {
      return this.events.filter(event => event.event_type === 'presence' || event.event_type === 'absence')
    },

    filteredEvents () {
      return this.events.filter(event => event.event_type !== 'presence' && event.event_type !== 'absence')
    },

    calendarsWithPresence () {
      return this.userCalendars.map(calendar => {
        const presenceAbsenceEvents = this.presenceAndAbsenceEvents.filter(event =>
          event.calendar_events.some(calEvent => calEvent.calendar_id === calendar.id)
        )

        // Tilføj right og left værdier
        const formattedEvents = presenceAbsenceEvents.map(event => {
          const top = this.eventTop(event)
          const right = this.eventRight(event)
          const left = top // Hvis left skal være noget andet, tilpas det her
          const height = this.eventHeightInPx
          const bottom = this.eventBottom(event)

          return {
            ...event,
            top,
            bottom,
            left,
            right,
            height,
            vStyle: {
              backgroundColor: event.color,
              top: `${top}px`,
              bottom: `${bottom}px`,
              left: `${left}px`,
              right: `${right}px`,
              height: `${height}px`
            }
          }
        })

        return {
          ...calendar,
          vPresence: formattedEvents.filter(event => event.event_type === 'presence'),
          vAbsence: formattedEvents.filter(event => event.event_type === 'absence')
        }
      })
    },

    calendarsWithEventsHash () {
      const calendarMap = {}

      this.filteredEvents.forEach(event => {
        event.calendar_events.forEach(calEvent => {
          if (!calendarMap[calEvent.calendar_id]) {
            calendarMap[calEvent.calendar_id] = []
          }

          const top = this.eventTop(event)
          const height = this.eventHeightInPx
          const bottom = this.eventBottom(event)
          const right = this.eventRight(event)

          const eventData = {
            ...event,
            vColumns: 1,
            top,
            bottom,
            left: top,
            height,
            right,
            vStyle: {
              backgroundColor: event.color,
              top: 0,
              bottom: '5px',
              left: `${top}px`,
              height: `${height}px`,
              right: `${right}px`
            }
          }
          calendarMap[calEvent.calendar_id].push(eventData)
        })
      })

      Object.keys(calendarMap).forEach(calendarId => {
        let events = calendarMap[calendarId]
        events.sort((e1, e2) => e1.top - e2.top || e1.bottom - e2.bottom)

        let columns = []

        events.forEach(event => {
          let placed = false

          for (let column of columns) {
            if (!column.some(e => this.collidesWith(e, event))) {
              column.push(event)
              placed = true
              break
            }
          }

          if (!placed) {
            columns.push([event])
          }
        })

        columns.forEach((column, colIndex) => {
          let colWidth = (this.eventHeightInPx * colIndex) + 1
          if (colIndex > 0) {
            colWidth = colWidth + 2
          }
          column.forEach(event => {
            event.vStyle.top = `${colWidth}px`
            event.vColumns = columns.length
          })
        })
      })

      return calendarMap
    },

    calendarsWithEvents () {
      return this.userCalendars.map(calendar => {
        const events = this.calendarsWithEventsHash[calendar.id] || []
        const presenceAbsence = this.calendarsWithPresence.find(pCal => pCal.id === calendar.id) || {}

        return {
          ...calendar,
          vEvents: events.sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf()),
          vPresence: presenceAbsence.vPresence || [],
          vAbsence: presenceAbsence.vAbsence || [],
          vColumns: this.findLargestColumn(events)
        }
      })
    }
  },
  methods: {
    calculateMaxHeight () {
      if (this.$refs.doScheduler) {
        const topOffset = this.$refs.doScheduler.getBoundingClientRect().top
        this.schedulerMaxHeight = window.innerHeight - topOffset - 16 // 1rem ≈ 16px
      }
    },
    getEventStyle (event) {
      return Object.assign({}, event.vStyle, {'--event-color': event.color})
    },
    findLargestColumn (events) {
      return events.reduce((max, event) => event.vColumns > max ? event.vColumns : max, 1)
    },
    eventRight (event) {
      let time = moment.duration({
        minutes: moment(event.end).format('mm'),
        hours: moment(event.end).format('HH')
      }).asMinutes()
      let endOfDay = moment.duration({
        hours: 24,
        minutes: 0
      }).asMinutes()
      return ((this.slotWidth * 2) / 60) * (endOfDay - time)
    },
    eventTop (event) {
      let calStartTime = moment(event.start).set({
        'hour': 0,
        'minutes': 0
      })
      let eventStartTime = moment(event.start)
      let duration = moment(eventStartTime).diff(calStartTime)
      return ((this.slotWidth * 2) / 60) * moment.duration(duration).asMinutes()
    },
    eventBottom (event) {
      let calStartTime = moment(event.end).set({
        'hour': 0,
        'minutes': 0
      })
      let eventEndTime = moment(event.end)
      let duration = moment(eventEndTime).diff(calStartTime)
      return ((this.slotWidth * 2) / 60) * moment.duration(duration).asMinutes()
    },
    // Check if two events collide
    collidesWith (a, b) {
      return a.bottom > b.top && a.top < b.bottom
    },
    packEvents (columns) {
      let n = columns.length
      for (let i = 0; i < n; i++) {
        let col = columns[i]
        for (let j = 0; j < col.length; j++) {
          let ev = col[j]
          let colSpan = this.expandEvent(ev, i, columns)
          col[j].row = i
          if ((i + 1) > this.rows) {
            this.rows = i + 1
          }
          col[j].colspan = colSpan / n
        }
      }
    },
    expandEvent (ev, iColumn, columns) {
      let colSpan = 1
      for (let i = iColumn + 1; i < columns.length; i++) {
        let col = columns[i]
        for (let j = 0; j < col.length; j++) {
          let ev1 = col[j]
          if (this.collidesWith(ev, ev1)) {
            return colSpan
          }
        }
        colSpan++
      }
      return colSpan
    },
    setWidthAndLeft () {
      this.isEventsHasSortedOnce = true
      let columns = []
      let lastEventEnding = null
      let events = this.sortedEvents.map(elem => {
        const top = this.eventTop(elem)
        const height = this.eventHeight(elem)
        const bottom = this.eventBottom(elem)
        const right = this.eventRight(elem)
        return {
          'obj': elem,
          'top': top,
          'left': top,
          'right': right,
          'height': height,
          'bottom': bottom
        }
      })
      events.sort(function (e1, e2) {
        if (e1.top < e2.top) return -1
        if (e1.top > e2.top) return 1
        if (e1.bottom < e2.bottom) return -1
        if (e1.bottom > e2.bottom) return 1
        return 0
      })
      for (let i = 0; i < events.length; i++) {
        let e = events[i]
        if (lastEventEnding !== null && e.top >= lastEventEnding) {
          this.packEvents(columns)
          columns = []
          lastEventEnding = null
        }
        let placed = false
        for (let j = 0; j < columns.length; j++) {
          let col = columns[j]
          if (!this.collidesWith(col[col.length - 1], e)) {
            col.push(e)
            placed = true
            break
          }
        }
        if (!placed) {
          columns.push([e])
        }
        if (lastEventEnding === null || e.bottom > lastEventEnding) {
          lastEventEnding = e.bottom
        }
      }
      if (columns.length > 0) {
        this.packEvents(columns)
      }
    }
  },
  mounted () {
    this.calculateMaxHeight()
    window.addEventListener('resize', this.calculateMaxHeight)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.calculateMaxHeight)
  }
}
</script>

<style scoped>

.do-scheduler {
  max-width: calc(100vw - 65px - 2rem);
  overflow-x: auto;
}

.scheduler__header__times {
  position: sticky;
  z-index: 10;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
}

.scheduler__header__times > li {
  background: #fff;
  border: 0 solid #ebeff3;
  border-width: 0 1px 1px 0;
  text-align: left;
  padding: 4px;
}

.do-scheduler__list__item {
  position: relative;
  width: 100%;
  z-index: 1;
  background: #fff;
  border-bottom: 1px solid #eee;
}

.do-scheduler__list__item:nth-child(2n),
.do-scheduler__list__item:nth-child(2n) > .do-scheduler__list__item__name {
  background-color: #f9fafb;
}

.do-scheduler__list__item__name {
  position: sticky;
  z-index: 2;
  top: 0;
  left: 0;
  height: 100%;
  width: 200px;
  background-color: #fff;
  display: flex;
  align-items: center;
  padding: 0 0.25rem;
  border-right: 1px solid #ddd
}

.do-scheduler__item__events {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 100%;
}

.do-scheduler__item__events__event {
  position: absolute;
  overflow: hidden;
  font-size: 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 2px 1px 2px 6px;
  color: #fff;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, .14), 0 3px 1px -2px rgba(0, 0, 0, .12), 0px 0px 0px 0 rgba(0, 0, 0, .2);
  border-radius: 2px;
}

.do-scheduler__item__events__event > div {
  line-height: 1;
}

.do-scheduler__item__events__event:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  bottom: 0;
  opacity: .8;
  background-color: var(--event-color);
  border-left: 1px solid #fff;
  border-radius: 2px 0 0 2px;
}

.do-scheduler__item__events__event:after {
  content: '';
  position: absolute;
  left: 4px;
  right: 0;
  bottom: 0;
  top: 0;
}

.do-scheduler__item__events__event__title {
  font-weight: bold;
}

.do-scheduler__item__events__presence {
  position: absolute;
  top: 0;
  height: calc(100% - 3px);
  bottom: 0;
  background: rgba(153, 197, 244, .4);
}
</style>



// WEBPACK FOOTER //
// Scheduler.vue?14166074